import { inject, injectable } from '@servicetitan/react-ioc';
import { AcceptLegalAgreementRequest, Agreement, LegalAgreementApi } from 'api';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { PortalAuthStore } from '../../auth/stores/portal-auth.store';

@injectable()
export class LegalAgreementStore {
    @observable agreement: Agreement | undefined;
    @observable loading = true;
    @observable accepting = false;
    @observable loadError = false;
    @observable acceptError = false;

    private inFlight: Promise<void> | undefined;

    constructor(
        @inject(LegalAgreementApi) private api: LegalAgreementApi,
        @inject(PortalAuthStore) private auth: PortalAuthStore
    ) {
        makeObservable(this);
    }

    fetch = (): Promise<void> => {
        if (this.inFlight !== undefined) {
            return this.inFlight;
        }

        runInAction(() => {
            this.loading = true;
            this.loadError = false;
        });

        this.inFlight = (async () => {
            try {
                const agreement = await this.api.client.getLatest();
                runInAction(() => {
                    this.agreement = agreement;
                });
            } catch {
                runInAction(() => {
                    this.loadError = true;
                });
            } finally {
                runInAction(() => {
                    this.loading = false;
                });
                this.inFlight = undefined;
            }
        })();

        return this.inFlight;
    };

    @action
    accept = async (): Promise<void> => {
        if (!this.agreement || this.accepting) {
            return;
        }
        this.accepting = true;
        this.acceptError = false;
        try {
            await this.api.client.accept(
                new AcceptLegalAgreementRequest({ agreementId: this.agreement.id })
            );
            this.auth.markAgreementAccepted();
        } catch {
            runInAction(() => {
                this.acceptError = true;
            });
        } finally {
            runInAction(() => {
                this.accepting = false;
            });
        }
    };
}
